export const BUSINESS_DAYS_PER_LEAD_WEEK = 5;

const LEGACY_WEEK_DAY_MULTIPLIER = 7;

const LEGACY_WEEK_MULTIPLIER_MIN_STORED = 14;

export type LeadTimeUnit = 'days' | 'weeks';

export function normalizeStoredLeadTimeWeeks(
  value: number | null | undefined,
): number | null {
  if (value == null || !Number.isFinite(value) || value <= 0) {
    return null;
  }
  const rounded = Math.round(value);
  if (
    rounded % LEGACY_WEEK_DAY_MULTIPLIER === 0 &&
    rounded >= LEGACY_WEEK_MULTIPLIER_MIN_STORED
  ) {
    return rounded / LEGACY_WEEK_DAY_MULTIPLIER;
  }
  return rounded;
}

export function pricingLeadTimeToFormFields(
  from: number | null | undefined,
  to: number | null | undefined,
): {
  fromAmount: number | null;
  toAmount: number | null;
  unit: LeadTimeUnit;
} {
  const nFrom = normalizeStoredLeadTimeWeeks(from);
  const nTo = normalizeStoredLeadTimeWeeks(to);
  if (nFrom == null && nTo == null) {
    return { fromAmount: null, toAmount: null, unit: 'weeks' };
  }
  if (nFrom == null) {
    return { fromAmount: nTo, toAmount: nTo, unit: 'weeks' };
  }
  if (nTo == null) {
    return { fromAmount: nFrom, toAmount: nFrom, unit: 'weeks' };
  }
  if (nFrom === nTo) {
    return { fromAmount: nFrom, toAmount: nTo, unit: 'weeks' };
  }
  if (nFrom > nTo || (nTo === 1 && nFrom > 1)) {
    const effective = Math.max(nFrom, nTo);
    return {
      fromAmount: effective,
      toAmount: effective,
      unit: 'weeks',
    };
  }
  const min = Math.min(nFrom, nTo);
  const max = Math.max(nFrom, nTo);
  if (min === 1 && max > 1) {
    return { fromAmount: max, toAmount: max, unit: 'weeks' };
  }
  if (min >= 2 && max > min) {
    return { fromAmount: min, toAmount: max, unit: 'weeks' };
  }
  return { fromAmount: max, toAmount: max, unit: 'weeks' };
}

export function leadAmountToStoredWeeks(
  amount: number,
  unit: LeadTimeUnit,
): number {
  const rounded = Math.round(amount);
  if (rounded <= 0) {
    return rounded;
  }
  if (unit === 'weeks') {
    return rounded;
  }
  return Math.max(1, Math.round(rounded / BUSINESS_DAYS_PER_LEAD_WEEK));
}

export function storedWeeksToFormInput(
  stored: number | null | undefined,
): { amount: number; unit: LeadTimeUnit } | null {
  const weeks = normalizeStoredLeadTimeWeeks(stored);
  if (weeks == null) {
    return null;
  }
  return { amount: weeks, unit: 'weeks' };
}

export function resolveEffectiveLeadTimeWeeks(
  from: number | null | undefined,
  to: number | null | undefined,
): number | null {
  const normalizedFrom = normalizeStoredLeadTimeWeeks(from);
  const normalizedTo = normalizeStoredLeadTimeWeeks(to);
  if (normalizedFrom == null && normalizedTo == null) {
    return null;
  }
  if (normalizedFrom == null) {
    return normalizedTo;
  }
  if (normalizedTo == null) {
    return normalizedFrom;
  }
  if (normalizedFrom === normalizedTo) {
    return normalizedFrom;
  }
  if (normalizedFrom > normalizedTo) {
    return normalizedFrom;
  }
  const min = Math.min(normalizedFrom, normalizedTo);
  const max = Math.max(normalizedFrom, normalizedTo);
  if (min === 1 && max > 1) {
    return max;
  }
  return max;
}

function formatSingleLeadTimeWeeks(weeks: number): string {
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
}

export function formatLeadTimeWeeksLabel(
  from: number | null | undefined,
  to: number | null | undefined,
): string {
  const normalizedFrom = normalizeStoredLeadTimeWeeks(from);
  const normalizedTo = normalizeStoredLeadTimeWeeks(to);
  if (normalizedFrom == null && normalizedTo == null) {
    return '—';
  }
  if (normalizedFrom == null || normalizedTo == null) {
    const single = normalizedFrom ?? normalizedTo!;
    return formatSingleLeadTimeWeeks(single);
  }
  if (normalizedFrom === normalizedTo) {
    return formatSingleLeadTimeWeeks(normalizedFrom);
  }
  if (normalizedFrom > normalizedTo) {
    return formatSingleLeadTimeWeeks(normalizedFrom);
  }
  const min = Math.min(normalizedFrom, normalizedTo);
  const max = Math.max(normalizedFrom, normalizedTo);
  if (min === 1 && max > 1) {
    return formatSingleLeadTimeWeeks(max);
  }
  if (min >= 2 && max > min) {
    return `${min}–${max} weeks`;
  }
  return formatSingleLeadTimeWeeks(max);
}
